<template>
  <main class="profit-preview__main">
    <div
      v-if="props.profitPreview.howToWork"
      class="profit-preview__main-section"
    >
      <div class="profit-preview__main-content">
        <Heading :level="3" class="profit-preview__section-title">
          {{ props.profitPreview.howToWork.title }}
        </Heading>
        <BaseText size="p4">
          {{ props.profitPreview.howToWork.description }}
        </BaseText>
      </div>
    </div>
    <div
      v-if="props.profitPreview.benefits"
      class="profit-preview__main-section"
    >
      <div class="profit-preview__main-content">
        <Heading :level="3" class="profit-preview__section-title">
          {{ props.profitPreview.benefits.title }}
        </Heading>
        <Row
          v-for="(benefit, index) in props.profitPreview.benefits.options"
          :key="benefit.title"
          :icon="benefit.icon"
          :has-line="index !== 0"
        >
          <template #icon-left>
            <InitialAvatarIcon
              class="profit-preview__benefits-icon"
              :icon="benefit.icon"
              size="small"
            />
          </template>
          <template #top-left>
            <BaseText size="p4" :color="NebraskaColors.elementPrimary">
              {{ benefit.title }}
            </BaseText>
          </template>
        </Row>
      </div>
    </div>
    <div v-if="props.profitPreview.costs" class="profit-preview__main-section">
      <div class="profit-preview__main-content">
        <Heading :level="3" class="profit-preview__section-title">
          {{ props.profitPreview.costs.title }}
        </Heading>
        <BaseText class="profit-preview__section-text" size="p4">
          {{ props.profitPreview.costs.description }}
        </BaseText>
        <router-link
          v-if="shouldShowRPLButton"
          :to="{ name: 'retailLiquidityProvider' }"
        >
          <ButtonTertiary
            :label="props.profitPreview.costs.button.text"
            :icon="props.profitPreview.costs.button.icon"
            icon-position="right"
          />
        </router-link>
      </div>
    </div>
    <div
      v-if="props.profitPreview.profit"
      class="profit-preview__main-section--highlight"
    >
      <div class="profit-preview__main-content">
        <ChipStatus
          :label="props.profitPreview.profit.status.text"
          class="profit-preview__chip"
          :status="isRLPEnabled ? 'positive' : 'negative'"
        />
        <HeadingDisplay
          :title="props.profitPreview.profit.value"
          :head="props.profitPreview.profit.title"
          :subtitle="props.profitPreview.profit.description"
          size="extra-small"
        >
          <template #title-component>
            <Heading :level="2">{{ props.profitPreview.profit.value }}</Heading>
          </template>
        </HeadingDisplay>
      </div>
    </div>
  </main>
</template>

<script setup lang="ts">
import {
  Heading,
  BaseText,
  Row,
  InitialAvatarIcon,
  ButtonTertiary,
  ChipStatus,
  HeadingDisplay
} from '@warrenbrasil/nebraska-web';
import { NebraskaColors } from '@warrenbrasil/nebraska-tokens-web';
import type { ProfitPreview } from '@/modules/trade/services/profit';
import { computed, onMounted } from 'vue';
import { tradeModule } from '@/modules/trade/store/trade';

interface Props {
  profitPreview: ProfitPreview;
}

const props = defineProps<Props>();

interface EmitEvents {
  (event: 'subscribe'): void;
}

defineEmits<EmitEvents>();

onMounted(async () => {
  await tradeModule.fetchStatus();
});

const isRLPEnabled = computed(() => {
  return tradeModule.tradeStatus?.isRLPEnabled;
});

const shouldShowRPLButton = computed(() => {
  const hasButton = props.profitPreview.costs.button !== null;

  return hasButton && !isRLPEnabled.value;
});
</script>

<style lang="less" scoped>
.profit-preview {
  &__main {
    margin: @size-spacing-x800 0;
  }

  &__main-section,
  &__main-section--highlight {
    padding: 0 @size-spacing-x1100;
    margin-bottom: @size-spacing-x800;
  }

  &__main-content {
    max-width: 801px;
    margin: auto;
  }

  &__main-section--highlight {
    padding: @size-spacing-x600 @size-spacing-x1100;
    background-color: @background-primary;
  }

  &__section-title {
    margin-bottom: @size-spacing-x400;
  }

  &__section-text {
    white-space: pre-line;
  }

  &__button-box-container {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: @size-spacing-x400;
    margin-top: @size-spacing-x800;
  }

  &__button-box {
    width: 392px;
  }

  &__benefits-icon {
    color: @status-positive;
    background-color: @status-positive-background;
  }

  &__chip {
    margin-bottom: @size-spacing-x300;
  }
}
</style>
